/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import { 
  Car, 
  Gauge, 
  Calendar, 
  Coins, 
  TrendingDown, 
  Sliders, 
  HelpCircle, 
  Sparkles, 
  ShieldCheck, 
  Award, 
  FileText, 
  Download, 
  Copy, 
  Check, 
  ChevronDown, 
  ChevronUp, 
  AlertTriangle, 
  BatteryCharging, 
  Fuel, 
  CheckCircle2, 
  Info, 
  ArrowRight, 
  Wrench, 
  Layers, 
  Building2, 
  Share2, 
  RotateCcw
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  Tooltip, 
  CartesianGrid, 
  ReferenceDot, 
  Legend 
} from 'recharts';
import { jsPDF } from 'jspdf';
import { EmbedCalloutBanner } from '../geldanlage-sparen/EmbedCalloutBanner';
import { Reviews } from '../Reviews';
import { RelatedCalculators } from '../steuer-rechner/RelatedCalculators';

export interface RestwertRechnerProps {
  onBackToPortal?: () => void;
  navigateTo?: (path: string) => void;
}

// Fahrzeugkategorien mit degressiven Abschreibungsraten (Jahr 1 bis 5+) nach DAT/Schwacke Marktdaten
interface VehicleCategoryConfig {
  id: string;
  name: string;
  icon: string;
  examples: string;
  yearRates: [number, number, number, number, number]; // Jahr 1, 2, 3, 4, 5+
  avgKmPerYear: number;
}

const VEHICLE_CATEGORIES: VehicleCategoryConfig[] = [
  {
    id: 'kleinwagen',
    name: 'Kleinwagen',
    icon: '🚗',
    examples: 'z.B. VW Polo, Opel Corsa, Toyota Yaris, Mini Cooper',
    yearRates: [0.24, 0.12, 0.10, 0.08, 0.06],
    avgKmPerYear: 11000
  },
  {
    id: 'kompaktklasse',
    name: 'Kompaktklasse',
    icon: '🚙',
    examples: 'z.B. VW Golf, Ford Focus, Opel Astra, Audi A3, Seat Leon',
    yearRates: [0.22, 0.11, 0.09, 0.07, 0.06],
    avgKmPerYear: 14000
  },
  {
    id: 'mittelklasse',
    name: 'Mittelklasse',
    icon: '🚘',
    examples: 'z.B. BMW 3er, Audi A4, Mercedes C-Klasse, VW Passat',
    yearRates: [0.20, 0.10, 0.08, 0.06, 0.05],
    avgKmPerYear: 18000
  },
  {
    id: 'oberklasse',
    name: 'Oberklasse',
    icon: '🏎️',
    examples: 'z.B. Mercedes S-Klasse, BMW 7er, Audi A8, Porsche Panamera',
    yearRates: [0.25, 0.13, 0.10, 0.08, 0.06],
    avgKmPerYear: 19000
  },
  {
    id: 'suv',
    name: 'SUV / Geländewagen',
    icon: '🚐',
    examples: 'z.B. VW Tiguan, BMW X3, Audi Q5, Mercedes GLC, Ford Kuga',
    yearRates: [0.18, 0.10, 0.08, 0.06, 0.05],
    avgKmPerYear: 15000
  },
  {
    id: 'sportwagen',
    name: 'Sportwagen',
    icon: '🏎️',
    examples: 'z.B. Porsche 911, BMW Z4, Mazda MX-5, Audi TT',
    yearRates: [0.15, 0.08, 0.07, 0.06, 0.04],
    avgKmPerYear: 9000
  },
  {
    id: 'transporter',
    name: 'Transporter / Van',
    icon: '🚐',
    examples: 'z.B. VW Multivan/T6, Mercedes V-Klasse, Ford Tourneo',
    yearRates: [0.16, 0.10, 0.08, 0.07, 0.06],
    avgKmPerYear: 20000
  },
  {
    id: 'elektroauto',
    name: 'Elektroauto (BEV)',
    icon: '🔋',
    examples: 'z.B. Tesla Model 3/Y, VW ID.4, Hyundai Ioniq 5, BMW i4',
    yearRates: [0.28, 0.14, 0.10, 0.08, 0.06],
    avgKmPerYear: 13500
  }
];

export function RestwertRechner({ onBackToPortal, navigateTo }: RestwertRechnerProps) {
  // Grunddaten
  const [purchasePrice, setPurchasePrice] = useState<number>(35000);
  const [ageYears, setAgeYears] = useState<number>(3);
  const [ageMonths, setAgeMonths] = useState<number>(0);
  const [mileageKm, setMileageKm] = useState<number>(45000);
  const [category, setCategory] = useState<string>('kompaktklasse');

  // Zustand & Ausstattung
  const [condition, setCondition] = useState<string>('gut'); // 'sehr_gut' (+5%), 'gut' (0%), 'befriedigend' (-8%), 'maessig' (-18%)
  const [equipmentLevel, setEquipmentLevel] = useState<string>('standard'); // 'basis' (-5%), 'standard' (0%), 'komfort' (+5%), 'premium' (+12%)

  // Detailfaktoren (Smart-Rechnr 2026/2027 Extras)
  const [fuelType, setFuelType] = useState<string>('benzin'); // 'benzin', 'diesel', 'hybrid', 'elektro'
  const [batteryHealth, setBatteryHealth] = useState<string>('normal'); // 'top' (>90%), 'normal' (80-89%), 'degraded' (<80%)
  const [serviceHistory, setServiceHistory] = useState<string>('hersteller'); // 'hersteller' (+4%), 'frei' (0%), 'lueckenhaft' (-8%)
  const [previousOwners, setPreviousOwners] = useState<number>(1); // 1. Hand (+3%), 2. Hand (0%), 3+ (-5%)
  const [tuevStatus, setTuevStatus] = useState<string>('neu'); // 'neu' (+2%), 'gueltig' (0%), 'faellig' (-4%)

  // UI States
  const [activeTab, setActiveTab] = useState<'uebersicht' | 'prognose' | 'tabelle' | 'vergleich'>('uebersicht');
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [copiedEmbed, setCopiedEmbed] = useState<boolean>(false);
  const [showEmbedCode, setShowEmbedCode] = useState<boolean>(false);

  // Gefundene Kategorie
  const catConfig = useMemo(() => {
    return VEHICLE_CATEGORIES.find(c => c.id === category) || VEHICLE_CATEGORIES[1];
  }, [category]);

  // Gesamtes Alter in Jahren
  const totalAgeYears = useMemo(() => {
    return Math.max(0.08, ageYears + ageMonths / 12);
  }, [ageYears, ageMonths]);

  // Durchschnittliche erwartete Kilometerleistung
  const expectedMileage = useMemo(() => {
    return Math.round(catConfig.avgKmPerYear * totalAgeYears);
  }, [catConfig, totalAgeYears]);

  // Rechner-Engine
  const results = useMemo(() => {
    const P0 = purchasePrice;
    const rates = catConfig.yearRates;

    // 1. Degressiver Basis-Wertverlust über die Jahre
    // Berechne für ganzzahlige Jahre und linearen Anteil für Restmonate
    let baseFactor = 1.0;
    const fullYears = Math.floor(totalAgeYears);
    const fraction = totalAgeYears - fullYears;

    for (let y = 0; y < fullYears; y++) {
      const rate = y < 4 ? rates[y] : rates[4];
      baseFactor *= (1 - rate);
    }

    if (fraction > 0) {
      const nextRate = fullYears < 4 ? rates[fullYears] : rates[4];
      // Kontinuierliche Abschreibung für Restmonate
      baseFactor *= (1 - nextRate * fraction);
    }

    // 2. Kilometerkorrektur
    const kmDiff = mileageKm - expectedMileage;
    let kmFactorAdjustment = 0;
    if (kmDiff < 0) {
      // Weniger km als Schnitt: Werterhöhung (ca. +0.3% pro 1.000 km)
      kmFactorAdjustment = Math.min(0.14, (Math.abs(kmDiff) / 1000) * 0.003);
    } else {
      // Mehr km als Schnitt: Wertminderung (ca. -0.4% pro 1.000 km)
      kmFactorAdjustment = -Math.min(0.30, (kmDiff / 1000) * 0.004);
    }

    // 3. Zustandsfaktor
    let conditionFactor = 1.0;
    if (condition === 'sehr_gut') conditionFactor = 1.05;
    else if (condition === 'befriedigend') conditionFactor = 0.92;
    else if (condition === 'maessig') conditionFactor = 0.82;

    // 4. Ausstattungsfaktor
    let equipFactor = 1.0;
    if (equipmentLevel === 'basis') equipFactor = 0.95;
    else if (equipmentLevel === 'komfort') equipFactor = 1.05;
    else if (equipmentLevel === 'premium') equipFactor = 1.12;

    // 5. Zusatzfaktoren (Service, Vorbesitzer, TÜV, Batterie/Kraftstoff)
    let extraFactor = 1.0;
    if (serviceHistory === 'hersteller') extraFactor += 0.04;
    else if (serviceHistory === 'lueckenhaft') extraFactor -= 0.08;

    if (previousOwners === 1) extraFactor += 0.03;
    else if (previousOwners >= 3) extraFactor -= 0.05;

    if (tuevStatus === 'neu') extraFactor += 0.02;
    else if (tuevStatus === 'faellig') extraFactor -= 0.05;

    if (category === 'elektroauto' || fuelType === 'elektro') {
      if (batteryHealth === 'top') extraFactor += 0.05;
      else if (batteryHealth === 'degraded') extraFactor -= 0.12;
    }

    // Multiplikativer Gesamt-Fahrzeugwert (Marktpreis / Händlerverkauf)
    const combinedFactor = baseFactor * (1 + kmFactorAdjustment) * conditionFactor * equipFactor * extraFactor;
    // Realistischer Bodenwert (mindestens 5% des Neupreises oder 500 €)
    const effectiveFactor = Math.max(0.04, combinedFactor);
    const marketPrice = Math.round(P0 * effectiveFactor);

    // 3-stufiger Marktpreis
    const dealerBuyPrice = Math.round(marketPrice * 0.85); // Händler-Einkauf (Inzahlungnahme)
    const privateSalePrice = Math.round(marketPrice * 1.08); // Privatverkauf (Verhandlungsbasis)

    // Gesamter Wertverlust
    const totalLoss = P0 - marketPrice;
    const totalLossPercent = ((totalLoss / P0) * 100);

    // Zeit- und Kilometerkosten
    const totalMonths = Math.max(1, Math.round(totalAgeYears * 12));
    const lossPerMonth = Math.round(totalLoss / totalMonths);
    const lossPerYear = Math.round(totalLoss / Math.max(0.5, totalAgeYears));
    const lossPerKm = mileageKm > 0 ? (totalLoss / mileageKm) * 100 : 0; // Cent pro km

    // Prognose für das nächste Jahr (12 Monate weiter, + avgKmPerYear)
    const nextYearAge = totalAgeYears + 1;
    const nextRate = nextYearAge <= 4 ? rates[Math.min(3, Math.floor(nextYearAge))] : rates[4];
    const expectedLossNextYear = Math.round(marketPrice * nextRate);
    const marketPriceNextYear = Math.max(Math.round(P0 * 0.03), marketPrice - expectedLossNextYear);
    const nextYearLossPercent = ((expectedLossNextYear / marketPrice) * 100);

    // Steuerlicher Buchwert nach § 7 Abs. 1 EStG (Lineare AfA über 6 Jahre = 72 Monate)
    const afaMonths = 72;
    const elapsedMonths = Math.min(afaMonths, Math.round(totalAgeYears * 12));
    const afaBookValue = Math.max(1, Math.round(P0 * (1 - elapsedMonths / afaMonths)));
    const silentReserve = marketPrice - afaBookValue;

    // 10-Jahres-Verlaufstabelle
    const trajectoryData = [];
    for (let yr = 0; yr <= 10; yr++) {
      let f = 1.0;
      for (let y = 0; y < yr; y++) {
        f *= (1 - (y < 4 ? rates[y] : rates[4]));
      }
      const mPrice = Math.round(P0 * f);
      const dPrice = Math.round(mPrice * 0.85);
      const pPrice = Math.round(mPrice * 1.08);
      const bValue = Math.max(1, Math.round(P0 * Math.max(0, 1 - (yr * 12) / 72)));

      trajectoryData.push({
        year: `Jahr ${yr}`,
        yearNum: yr,
        marktwert: mPrice,
        haendlerEinkauf: dPrice,
        privatverkauf: pPrice,
        buchwert: bValue,
        prozent: Math.round(f * 100)
      });
    }

    return {
      marketPrice,
      dealerBuyPrice,
      privateSalePrice,
      totalLoss,
      totalLossPercent,
      lossPerMonth,
      lossPerYear,
      lossPerKm,
      expectedMileage,
      kmDiff,
      marketPriceNextYear,
      expectedLossNextYear,
      nextYearLossPercent,
      afaBookValue,
      silentReserve,
      trajectoryData,
      combinedFactor
    };
  }, [purchasePrice, catConfig, totalAgeYears, mileageKm, expectedMileage, condition, equipmentLevel, serviceHistory, previousOwners, tuevStatus, category, fuelType, batteryHealth]);

  // Preset-Lader
  const applyPreset = (presetName: string) => {
    switch (presetName) {
      case 'golf':
        setPurchasePrice(35000);
        setAgeYears(3);
        setAgeMonths(0);
        setMileageKm(45000);
        setCategory('kompaktklasse');
        setFuelType('benzin');
        setCondition('gut');
        setEquipmentLevel('standard');
        setPreviousOwners(1);
        break;
      case 'bmw3':
        setPurchasePrice(58000);
        setAgeYears(4);
        setAgeMonths(2);
        setMileageKm(72000);
        setCategory('mittelklasse');
        setFuelType('diesel');
        setCondition('sehr_gut');
        setEquipmentLevel('premium');
        setPreviousOwners(1);
        break;
      case 'tesla':
        setPurchasePrice(46000);
        setAgeYears(2);
        setAgeMonths(6);
        setMileageKm(36000);
        setCategory('elektroauto');
        setFuelType('elektro');
        setBatteryHealth('top');
        setCondition('gut');
        setEquipmentLevel('standard');
        setPreviousOwners(1);
        break;
      case 'tiguan':
        setPurchasePrice(44000);
        setAgeYears(3);
        setAgeMonths(6);
        setMileageKm(52000);
        setCategory('suv');
        setFuelType('diesel');
        setCondition('gut');
        setEquipmentLevel('komfort');
        setPreviousOwners(1);
        break;
      case 'porsche':
        setPurchasePrice(135000);
        setAgeYears(4);
        setAgeMonths(0);
        setMileageKm(32000);
        setCategory('sportwagen');
        setFuelType('benzin');
        setCondition('sehr_gut');
        setEquipmentLevel('premium');
        setPreviousOwners(1);
        break;
      default:
        break;
    }
  };

  // PDF-Report Generator
  const downloadPdfReport = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let y = 18;

      // Header
      doc.setFillColor(30, 58, 138); // Dark Navy Blue
      doc.rect(0, 0, pageWidth, 28, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('FAHRZEUG-RESTWERT-GUTACHTEN & BEWERTUNG', 14, 13);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`smart-rechnr.de/restwert-rechner  |  Erstellt am: ${new Date().toLocaleDateString('de-DE')}  |  Gültig für 2026/2027`, 14, 21);

      y = 38;
      doc.setTextColor(33, 37, 41);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('1. Fahrzeugdaten & Eckdaten', 14, y);
      y += 6;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Kategorie: ${catConfig.name} (${catConfig.examples})`, 14, y); y += 5;
      doc.text(`Ursprünglicher Neupreis (Bruttolistenpreis): ${purchasePrice.toLocaleString('de-DE')} €`, 14, y); y += 5;
      doc.text(`Fahrzeugalter: ${ageYears} Jahre, ${ageMonths} Monate (${totalAgeYears.toFixed(1)} Jahre)`, 14, y); y += 5;
      doc.text(`Kilometerstand: ${mileageKm.toLocaleString('de-DE')} km (Norm-Laufleistung: ${expectedMileage.toLocaleString('de-DE')} km)`, 14, y); y += 5;
      doc.text(`Fahrzeugzustand: ${condition.toUpperCase()}  |  Ausstattung: ${equipmentLevel.toUpperCase()}`, 14, y); y += 5;
      doc.text(`Vorbesitzer: ${previousOwners}. Hand  |  Service: ${serviceHistory}  |  TÜV: ${tuevStatus}`, 14, y); y += 9;

      // Ergebnisse
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('2. Schätzergebnis: Marktwerte & Verkaufskanäle', 14, y);
      y += 7;

      // Result Box
      doc.setFillColor(243, 244, 246);
      doc.roundedRect(14, y, pageWidth - 28, 38, 2, 2, 'F');

      doc.setFontSize(11);
      doc.setTextColor(17, 24, 39);
      doc.text('FAHRZEUG-MARKTPREIS (Referenzwert):', 20, y + 10);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(37, 99, 235);
      doc.text(`${results.marketPrice.toLocaleString('de-DE')} € (${(results.combinedFactor * 100).toFixed(1)} % des Neupreises)`, 20, y + 19);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(75, 85, 99);
      doc.text(`Händler-Einkaufswert (Inzahlungnahme): ${results.dealerBuyPrice.toLocaleString('de-DE')} €`, 20, y + 27);
      doc.text(`Privatverkaufspreis (Verhandlungsbasis): ${results.privateSalePrice.toLocaleString('de-DE')} €`, 20, y + 33);
      y += 46;

      // 3. Wertverlust-Statistik
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(33, 37, 41);
      doc.text('3. Wertverlust- und Kostenanalyse', 14, y);
      y += 6;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Bisheriger Gesamtwertverlust: -${results.totalLoss.toLocaleString('de-DE')} € (-${results.totalLossPercent.toFixed(1)} %)`, 14, y); y += 5;
      doc.text(`Monatlicher Wertverlust: ca. ${results.lossPerMonth.toLocaleString('de-DE')} € / Monat`, 14, y); y += 5;
      doc.text(`Kosten pro gefahrenem Kilometer: ${results.lossPerKm.toFixed(1)} Cent / km`, 14, y); y += 5;
      doc.text(`Prognose für nächstes Jahr: ca. ${results.marketPriceNextYear.toLocaleString('de-DE')} € (-${results.expectedLossNextYear.toLocaleString('de-DE')} € p.a.)`, 14, y); y += 5;
      doc.text(`Steuerlicher Buchwert (§ 7 EStG AfA, 6 Jahre): ${results.afaBookValue.toLocaleString('de-DE')} € (Stille Reserve: ${results.silentReserve > 0 ? '+' : ''}${results.silentReserve.toLocaleString('de-DE')} €)`, 14, y); y += 9;

      // 4. Tipps für den Verkauf
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('4. Handlungsempfehlungen vor Verkauf oder Inzahlungnahme', 14, y);
      y += 6;
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.text('• Fahrzeugaufbereitung: Eine gründliche Innen- und Außenreinigung bringt erfahrungsgemäß 300 - 800 € Mehrerlös.', 14, y); y += 4.5;
      doc.text('• Frischer TÜV: Fahrzeuge mit mindestens 18 Monaten Rest-TÜV verkaufen sich deutlich schneller und teurer.', 14, y); y += 4.5;
      doc.text('• Scheckheft & Belege: Dokumentierte Wartungen und Rechnungen schaffen maximales Käufervertrauen.', 14, y); y += 4.5;
      doc.text('• Kaufvertrag: Beim Privatverkauf immer den Muster-Kaufvertrag mit Gewährleistungsausschluss nutzen.', 14, y); y += 8;

      // Footer
      doc.setFontSize(7.5);
      doc.setTextColor(156, 163, 175);
      doc.text('Hinweis: Diese Bewertung basiert auf statistischen Wertverlustkurven (Schwacke/DAT/ADAC Standard). Für ein rechtssicheres Unfall- oder Sachverständigengutachten wenden Sie sich an DEKRA oder TÜV.', 14, 285);

      doc.save(`Restwert-Gutachten-${catConfig.id}-${mileageKm}km.pdf`);
    } catch (err) {
      console.error('Fehler beim PDF-Export:', err);
    }
  };

  const embedCodeString = `<iframe src="https://smart-rechnr.de/restwert-rechner" width="100%" height="900" style="border:none;border-radius:12px;box-shadow:0 4px 20px rgba(0,0,0,0.08);" title="Restwert-Rechner: Auto-Wertverlust berechnen"></iframe>`;

  const copyEmbedToClipboard = () => {
    navigator.clipboard.writeText(embedCodeString);
    setCopiedEmbed(true);
    setTimeout(() => setCopiedEmbed(false), 2500);
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-4 py-6 md:py-10">
      {/* Navigation / Breadcrumb */}
      <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm text-slate-500 mb-6">
        <button 
          onClick={() => navigateTo ? navigateTo('/') : onBackToPortal?.()}
          className="hover:text-blue-600 transition-colors"
        >
          Startseite
        </button>
        <span aria-hidden="true">/</span>
        <button 
          onClick={() => navigateTo ? navigateTo('/#kfz') : onBackToPortal?.()}
          className="hover:text-blue-600 transition-colors"
        >
          Mobilität & KFZ
        </button>
        <span aria-hidden="true">/</span>
        <span className="text-slate-900 font-medium">Restwert-Rechner</span>
      </div>

      {/* Hero Section */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mb-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6 md:p-8 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-blue-700 mb-2">
              <span>Mobilitäts- & Gebrauchtwagenmarkt 2026 / 2027</span>
              <span aria-hidden="true">·</span>
              <span>DAT & Schwacke Orientierung</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              Restwert-Rechner: Auto-Wertverlust berechnen
            </h1>
            <p className="text-slate-600 text-sm md:text-base leading-relaxed mb-6">
              Ermitteln Sie präzise den aktuellen Marktwert, Händler-Einkaufspreis und Privatverkaufswert Ihres Fahrzeugs nach Alter, Kilometerstand, Ausstattung und Pflegezustand. Inklusive degressiver Abschreibungskurve und Prognose für 2027.
            </p>

            {/* Schnell-Presets */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-500 font-medium mr-1">Beliebte Beispiele:</span>
              <button 
                onClick={() => applyPreset('golf')}
                className="px-2.5 py-1 text-xs bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-700 rounded-lg transition-colors font-medium"
              >
                VW Golf (Kompakt)
              </button>
              <button 
                onClick={() => applyPreset('bmw3')}
                className="px-2.5 py-1 text-xs bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-700 rounded-lg transition-colors font-medium"
              >
                BMW 3er (Mittelklasse)
              </button>
              <button 
                onClick={() => applyPreset('tiguan')}
                className="px-2.5 py-1 text-xs bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-700 rounded-lg transition-colors font-medium"
              >
                VW Tiguan (SUV)
              </button>
              <button 
                onClick={() => applyPreset('tesla')}
                className="px-2.5 py-1 text-xs bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-700 rounded-lg transition-colors font-medium"
              >
                Tesla Model 3 (Elektro)
              </button>
              <button 
                onClick={() => applyPreset('porsche')}
                className="px-2.5 py-1 text-xs bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-700 rounded-lg transition-colors font-medium"
              >
                Porsche 911 (Sportwagen)
              </button>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="rounded-xl overflow-hidden shadow-md border border-slate-200">
              <img 
                src="/images/restwert-rechner.webp" 
                alt="Restwert-Rechner 2026 und 2027 – Professionelle Fahrzeugbewertung und Auto-Wertverlust" 
                className="w-full h-auto object-cover transform hover:scale-105 transition-transform duration-500"
                loading="eager"
                width={600}
                height={338}
              />
            </div>
            <div className="text-[11px] text-slate-400 mt-1.5 text-right">
              Geprüfte Wertermittlung für Gebrauchtwagen nach DAT & ADAC Methodik
            </div>
          </div>
        </div>
      </div>

      {/* Rechner Hauptbereich */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
        {/* Eingabe-Panel (Links) */}
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-5 flex items-center gap-2">
              <Sliders className="w-5 h-5 text-blue-600" />
              1. Fahrzeug-Grunddaten
            </h2>

            {/* Neupreis */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="purchase-price" className="text-sm font-medium text-slate-700">
                  Neupreis (Listenpreis inkl. Sonderausstattung)
                </label>
                <span className="text-base font-bold text-blue-700">
                  {purchasePrice.toLocaleString('de-DE')} €
                </span>
              </div>
              <input 
                id="purchase-price"
                type="range"
                min="5000"
                max="180000"
                step="1000"
                value={purchasePrice}
                onChange={(e) => setPurchasePrice(Number(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600 mb-2"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>5.000 €</span>
                <span>80.000 €</span>
                <span>180.000 €</span>
              </div>
            </div>

            {/* Fahrzeugalter */}
            <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Fahrzeugalter (Jahre)
                </label>
                <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden">
                  <button 
                    onClick={() => setAgeYears(Math.max(0, ageYears - 1))}
                    className="px-3.5 py-2.5 bg-slate-50 hover:bg-slate-100 text-slate-700 font-bold text-sm"
                  >
                    −
                  </button>
                  <input 
                    type="number" 
                    min="0" 
                    max="20"
                    value={ageYears}
                    onChange={(e) => setAgeYears(Math.max(0, Number(e.target.value)))}
                    className="w-full text-center py-2 text-sm font-semibold text-slate-900 outline-none"
                  />
                  <button 
                    onClick={() => setAgeYears(Math.min(25, ageYears + 1))}
                    className="px-3.5 py-2.5 bg-slate-50 hover:bg-slate-100 text-slate-700 font-bold text-sm"
                  >
                    +
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Zusätzliche Monate
                </label>
                <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden">
                  <button 
                    onClick={() => setAgeMonths(Math.max(0, ageMonths - 1))}
                    className="px-3.5 py-2.5 bg-slate-50 hover:bg-slate-100 text-slate-700 font-bold text-sm"
                  >
                    −
                  </button>
                  <input 
                    type="number" 
                    min="0" 
                    max="11"
                    value={ageMonths}
                    onChange={(e) => setAgeMonths(Math.max(0, Math.min(11, Number(e.target.value))))}
                    className="w-full text-center py-2 text-sm font-semibold text-slate-900 outline-none"
                  />
                  <button 
                    onClick={() => setAgeMonths(Math.min(11, ageMonths + 1))}
                    className="px-3.5 py-2.5 bg-slate-50 hover:bg-slate-100 text-slate-700 font-bold text-sm"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* Kilometerstand */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-1.5">
                <label htmlFor="mileage-input" className="text-sm font-medium text-slate-700">
                  Aktueller Kilometerstand
                </label>
                <span className="text-xs text-slate-500">
                  Ø Norm ({totalAgeYears.toFixed(1)} Jahre): <strong className="text-slate-800">{expectedMileage.toLocaleString('de-DE')} km</strong>
                </span>
              </div>
              <div className="relative mb-2">
                <input 
                  id="mileage-input"
                  type="number"
                  min="0"
                  max="400000"
                  step="1000"
                  value={mileageKm}
                  onChange={(e) => setMileageKm(Math.max(0, Number(e.target.value)))}
                  className="w-full pl-4 pr-12 py-2.5 text-base font-semibold border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                <span className="absolute right-4 top-3 text-sm text-slate-400 font-medium">km</span>
              </div>
              <input 
                type="range"
                min="0"
                max="250000"
                step="2500"
                value={mileageKm}
                onChange={(e) => setMileageKm(Number(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>Wenigfahrer</span>
                <span>Durchschnitt ({expectedMileage.toLocaleString('de-DE')} km)</span>
                <span>Vielfahrer (200.000+ km)</span>
              </div>
            </div>

            {/* Fahrzeugkategorie */}
            <div className="mb-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Fahrzeugkategorie (Wertverlust-Profil)
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {VEHICLE_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => {
                      setCategory(cat.id);
                      if (cat.id === 'elektroauto') setFuelType('elektro');
                    }}
                    className={`p-2.5 text-left rounded-xl border transition-all ${
                      category === cat.id 
                        ? 'border-blue-600 bg-blue-50/70 text-blue-900 ring-1 ring-blue-500' 
                        : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <div className="text-xl mb-1">{cat.icon}</div>
                    <div className="text-xs font-bold truncate">{cat.name}</div>
                    <div className="text-[10px] text-slate-500 truncate">1. Jahr: -{Math.round(cat.yearRates[0] * 100)}%</div>
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2 italic">
                {catConfig.examples}
              </p>
            </div>
          </div>

          {/* Zustand & Ausstattung (Panel 2) */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-5 flex items-center gap-2">
              <Award className="w-5 h-5 text-blue-600" />
              2. Fahrzeugzustand & Ausstattung
            </h2>

            {/* Zustand */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Fahrzeugzustand
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { id: 'sehr_gut', label: 'Sehr gut', diff: '+5%', desc: 'Neuwertig, keine Mängel, scheckheft' },
                  { id: 'gut', label: 'Gut', diff: '±0%', desc: 'Normale Gebrauchsspuren, gepflegt' },
                  { id: 'befriedigend', label: 'Befriedigend', diff: '-8%', desc: 'Deutliche Gebrauchsspuren' },
                  { id: 'maessig', label: 'Mäßig', diff: '-18%', desc: 'Größere Mängel, Kratzer' }
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setCondition(item.id)}
                    className={`p-3 text-left rounded-xl border transition-all ${
                      condition === item.id 
                        ? 'border-blue-600 bg-blue-50/80 text-blue-950 font-medium ring-1 ring-blue-500' 
                        : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs font-bold">{item.label}</span>
                      <span className={`text-[11px] font-semibold ${item.diff.startsWith('+') ? 'text-emerald-600' : item.diff.startsWith('-') ? 'text-rose-600' : 'text-slate-500'}`}>
                        {item.diff}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-500 line-clamp-1">{item.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Ausstattungsniveau */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Ausstattungslinie
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { id: 'basis', label: 'Basis', diff: '-5%', desc: 'Minimalausstattung, Stoffsitze' },
                  { id: 'standard', label: 'Standard', diff: '±0%', desc: 'Beliebte Serienausstattung' },
                  { id: 'komfort', label: 'Komfort', diff: '+5%', desc: 'Sitzheizung, Navi, Assistenz' },
                  { id: 'premium', label: 'Premium', diff: '+12%', desc: 'Leder, Panorama, Top-Audio' }
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setEquipmentLevel(item.id)}
                    className={`p-3 text-left rounded-xl border transition-all ${
                      equipmentLevel === item.id 
                        ? 'border-blue-600 bg-blue-50/80 text-blue-950 font-medium ring-1 ring-blue-500' 
                        : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs font-bold">{item.label}</span>
                      <span className={`text-[11px] font-semibold ${item.diff.startsWith('+') ? 'text-emerald-600' : item.diff.startsWith('-') ? 'text-rose-600' : 'text-slate-500'}`}>
                        {item.diff}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-500 line-clamp-1">{item.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Spezifische Zusatzkriterien (2026/2027 Differenzierung) */}
            <div className="pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Wartungshistorie
                </label>
                <select
                  value={serviceHistory}
                  onChange={(e) => setServiceHistory(e.target.value)}
                  className="w-full text-xs py-2 px-2.5 border border-slate-200 rounded-lg bg-white text-slate-800 font-medium focus:ring-2 focus:ring-blue-500"
                >
                  <option value="hersteller">Lückenlos Vertragswerkstatt (+4%)</option>
                  <option value="frei">Regelmäßig freie Werkstatt (±0%)</option>
                  <option value="lueckenhaft">Lückenhaftes Scheckheft (-8%)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Vorbesitzer
                </label>
                <select
                  value={previousOwners}
                  onChange={(e) => setPreviousOwners(Number(e.target.value))}
                  className="w-full text-xs py-2 px-2.5 border border-slate-200 rounded-lg bg-white text-slate-800 font-medium focus:ring-2 focus:ring-blue-500"
                >
                  <option value={1}>1. Hand (Erstbesitz, +3%)</option>
                  <option value={2}>2. Hand (±0%)</option>
                  <option value={3}>3+ Hände (-5%)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  TÜV / HU & AU
                </label>
                <select
                  value={tuevStatus}
                  onChange={(e) => setTuevStatus(e.target.value)}
                  className="w-full text-xs py-2 px-2.5 border border-slate-200 rounded-lg bg-white text-slate-800 font-medium focus:ring-2 focus:ring-blue-500"
                >
                  <option value="neu">Neu (&gt; 18 Monate, +2%)</option>
                  <option value="gueltig">Gültig (6-18 Monate, ±0%)</option>
                  <option value="faellig">Bald fällig / Abgelaufen (-5%)</option>
                </select>
              </div>
            </div>

            {/* Batterie-Sonderoption bei Elektroautos */}
            {(category === 'elektroauto' || fuelType === 'elektro') && (
              <div className="mt-4 p-3.5 bg-amber-50/70 border border-amber-200 rounded-xl">
                <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-amber-900">
                  <BatteryCharging className="w-4 h-4 text-amber-600" />
                  Batteriegesundheit (State of Health - SoH) für Elektrofahrzeuge 2026/2027
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setBatteryHealth('top')}
                    className={`py-1.5 px-2.5 text-xs rounded-lg border font-medium ${batteryHealth === 'top' ? 'bg-white border-amber-500 text-amber-900 shadow-sm' : 'border-transparent text-amber-800 hover:bg-amber-100/60'}`}
                  >
                    SoH &gt; 90% (Zertifikat, +5%)
                  </button>
                  <button
                    type="button"
                    onClick={() => setBatteryHealth('normal')}
                    className={`py-1.5 px-2.5 text-xs rounded-lg border font-medium ${batteryHealth === 'normal' ? 'bg-white border-amber-500 text-amber-900 shadow-sm' : 'border-transparent text-amber-800 hover:bg-amber-100/60'}`}
                  >
                    SoH 80-89% (Normal, ±0%)
                  </button>
                  <button
                    type="button"
                    onClick={() => setBatteryHealth('degraded')}
                    className={`py-1.5 px-2.5 text-xs rounded-lg border font-medium ${batteryHealth === 'degraded' ? 'bg-white border-amber-500 text-amber-900 shadow-sm' : 'border-transparent text-amber-800 hover:bg-amber-100/60'}`}
                  >
                    SoH &lt; 80% (Verschleiß, -12%)
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Ergebnis-Panel (Rechts) */}
        <div className="lg:col-span-5 space-y-6">
          {/* Haupt-Restwertkarte */}
          <div className="bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white rounded-2xl p-6 shadow-xl border border-blue-900/40">
            <div className="flex justify-between items-start mb-4">
              <div>
                <span className="text-xs uppercase tracking-wider text-blue-300 font-semibold">
                  Geschätzter Fahrzeugwert (Marktpreis)
                </span>
                <div className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1 text-white">
                  {results.marketPrice.toLocaleString('de-DE')} €
                </div>
              </div>
              <div className="text-right">
                <span className="inline-block px-2.5 py-1 bg-blue-500/20 border border-blue-400/30 text-blue-200 text-xs font-bold rounded-lg">
                  {(results.combinedFactor * 100).toFixed(1)} %
                </span>
                <div className="text-[11px] text-blue-300/80 mt-1">vom Neupreis</div>
              </div>
            </div>

            {/* 3 Verkaufskanäle im direkten Vergleich */}
            <div className="grid grid-cols-3 gap-2.5 pt-4 pb-4 border-t border-blue-800/60 my-4 text-center">
              <div className="p-2.5 bg-white/5 rounded-xl border border-white/10">
                <div className="text-[11px] text-slate-300">Händler-Einkauf</div>
                <div className="text-sm md:text-base font-bold text-white mt-0.5">
                  {results.dealerBuyPrice.toLocaleString('de-DE')} €
                </div>
                <div className="text-[10px] text-slate-400">schnell, sicher</div>
              </div>

              <div className="p-2.5 bg-blue-600/30 rounded-xl border border-blue-400/40 ring-1 ring-blue-400/50">
                <div className="text-[11px] text-blue-200 font-semibold">Marktpreis</div>
                <div className="text-sm md:text-base font-bold text-blue-100 mt-0.5">
                  {results.marketPrice.toLocaleString('de-DE')} €
                </div>
                <div className="text-[10px] text-blue-200/80">Referenzwert</div>
              </div>

              <div className="p-2.5 bg-white/5 rounded-xl border border-white/10">
                <div className="text-[11px] text-slate-300">Privatverkauf</div>
                <div className="text-sm md:text-base font-bold text-white mt-0.5">
                  {results.privateSalePrice.toLocaleString('de-DE')} €
                </div>
                <div className="text-[10px] text-emerald-400">höchster Erlös</div>
              </div>
            </div>

            {/* Wertverlust-Übersicht */}
            <div className="space-y-2 pt-2 text-xs">
              <div className="flex justify-between items-center text-slate-300">
                <span>Gesamter Wertverlust:</span>
                <span className="font-bold text-rose-300">
                  −{results.totalLoss.toLocaleString('de-DE')} € (−{results.totalLossPercent.toFixed(1)}%)
                </span>
              </div>
              <div className="flex justify-between items-center text-slate-300">
                <span>Wertverlust pro Monat:</span>
                <span className="font-semibold text-white">~{results.lossPerMonth.toLocaleString('de-DE')} € / Monat</span>
              </div>
              <div className="flex justify-between items-center text-slate-300">
                <span>Wertverlust pro Kilometer:</span>
                <span className="font-semibold text-white">{results.lossPerKm.toFixed(1)} Cent / km</span>
              </div>
            </div>

            {/* Download Button */}
            <div className="mt-6 pt-4 border-t border-blue-800/50 flex gap-2">
              <button
                onClick={downloadPdfReport}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-blue-600 hover:bg-blue-500 text-white text-xs md:text-sm font-semibold rounded-xl transition-all shadow-md active:scale-98"
              >
                <Download className="w-4 h-4" />
                Wertgutachten als PDF speichern
              </button>
            </div>
          </div>

          {/* Prognose nächstes Jahr */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm mb-3">
              <Sparkles className="w-4 h-4 text-amber-500" />
              Prognose für die nächsten 12 Monate
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="p-3 bg-slate-50 rounded-xl">
                <div className="text-xs text-slate-500">Erwarteter Wertverlust</div>
                <div className="text-base font-bold text-rose-600 mt-0.5">
                  −{results.expectedLossNextYear.toLocaleString('de-DE')} €
                </div>
                <div className="text-[11px] text-slate-400">ca. −{results.nextYearLossPercent.toFixed(1)}%</div>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl">
                <div className="text-xs text-slate-500">Restwert in 1 Jahr</div>
                <div className="text-base font-bold text-slate-900 mt-0.5">
                  {results.marketPriceNextYear.toLocaleString('de-DE')} €
                </div>
                <div className="text-[11px] text-slate-400">bei ca. {(mileageKm + catConfig.avgKmPerYear).toLocaleString('de-DE')} km</div>
              </div>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              <strong>Tipp zur Wertstabilität:</strong> Der stärkste relative Wertverlust entsteht im 1. Jahr (bis zu 28%). Nach 3 bis 5 Jahren flacht die Kurve ab und stabilisiert sich bei ca. 5–8% pro Jahr.
            </p>
          </div>

          {/* Steuerlicher Buchwert (§ 7 EStG AfA) */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <Building2 className="w-4 h-4 text-slate-500" />
                Steuerlicher Buchwert (AfA 6 Jahre)
              </span>
              <span className="text-xs font-bold text-slate-900">
                {results.afaBookValue.toLocaleString('de-DE')} €
              </span>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Bei betrieblicher Nutzung nach amtlicher AfA-Tabelle (6 Jahre Nutzungsdauer). 
              {results.silentReserve > 0 ? (
                <span className="text-emerald-700 font-medium"> Stille Reserve beim Verkauf: +{results.silentReserve.toLocaleString('de-DE')} € (steuerpflichtiger Gewinn).</span>
              ) : (
                <span> Keine stillen Reserven vorhanden.</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Interaktiver Wertverlauf-Chart (1 bis 10 Jahre) */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm mb-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg md:text-xl font-bold text-slate-900 flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-blue-600" />
              10-Jahres-Wertverlustkurve ({catConfig.name})
            </h3>
            <p className="text-xs md:text-sm text-slate-500 mt-1">
              Typische degressive Abschreibung im Vergleich zwischen Privatverkauf, Marktwert und Händlereinkauf. Der rote Punkt markiert Ihr aktuelles Fahrzeugalter.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="flex items-center gap-1.5 text-slate-600">
              <span className="w-3 h-3 bg-blue-600 rounded-full inline-block"></span> Marktwert
            </span>
            <span className="flex items-center gap-1.5 text-slate-600 ml-2">
              <span className="w-3 h-3 bg-emerald-500 rounded-full inline-block"></span> Privat
            </span>
            <span className="flex items-center gap-1.5 text-slate-600 ml-2">
              <span className="w-3 h-3 bg-slate-400 rounded-full inline-block"></span> Händler
            </span>
          </div>
        </div>

        <div className="h-72 md:h-84 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={results.trajectoryData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="colorMarktwert" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0.0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} />
              <YAxis 
                tickFormatter={(val) => `${(val / 1000).toFixed(0)}k €`} 
                tick={{ fontSize: 11 }} 
              />
              <Tooltip 
                formatter={(val: any) => [`${Number(val).toLocaleString('de-DE')} €`, '']}
                labelFormatter={(label) => `Fahrzeugalter: ${label}`}
              />
              <Area 
                type="monotone" 
                dataKey="privatverkauf" 
                stroke="#10b981" 
                strokeWidth={2} 
                fillOpacity={0} 
                name="Privatverkauf" 
              />
              <Area 
                type="monotone" 
                dataKey="marktwert" 
                stroke="#2563eb" 
                strokeWidth={3} 
                fillOpacity={1} 
                fill="url(#colorMarktwert)" 
                name="Marktpreis" 
              />
              <Area 
                type="monotone" 
                dataKey="haendlerEinkauf" 
                stroke="#94a3b8" 
                strokeWidth={2} 
                strokeDasharray="4 4" 
                fillOpacity={0} 
                name="Händler-Einkauf" 
              />
              {/* Referenzpunkt für das aktuelle Fahrzeug */}
              <ReferenceDot 
                x={`Jahr ${Math.min(10, Math.round(totalAgeYears))}`} 
                y={results.marketPrice} 
                r={6} 
                fill="#dc2626" 
                stroke="#ffffff" 
                strokeWidth={2} 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Tabelle der Werte 1 bis 5 Jahre */}
        <div className="mt-8 overflow-x-auto">
          <table className="w-full text-xs text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-y border-slate-200 text-slate-700 font-semibold">
                <th className="py-2.5 px-3">Fahrzeugalter</th>
                <th className="py-2.5 px-3">Restwert in %</th>
                <th className="py-2.5 px-3">Marktwert (€)</th>
                <th className="py-2.5 px-3">Händler-Einkauf</th>
                <th className="py-2.5 px-3">Privatverkauf</th>
                <th className="py-2.5 px-3">Jährlicher Wertverlust</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {results.trajectoryData.slice(1, 7).map((row, idx) => (
                <tr 
                  key={row.year} 
                  className={`hover:bg-blue-50/50 transition-colors ${Math.round(totalAgeYears) === idx + 1 ? 'bg-blue-50/80 font-semibold' : ''}`}
                >
                  <td className="py-2 px-3 font-medium text-slate-900 flex items-center gap-1.5">
                    {row.year}
                    {Math.round(totalAgeYears) === idx + 1 && (
                      <span className="text-[10px] text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">Aktuell</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-slate-700">{row.prozent} %</td>
                  <td className="py-2 px-3 text-blue-700 font-bold">{row.marktwert.toLocaleString('de-DE')} €</td>
                  <td className="py-2 px-3 text-slate-600">{row.haendlerEinkauf.toLocaleString('de-DE')} €</td>
                  <td className="py-2 px-3 text-emerald-700">{row.privatverkauf.toLocaleString('de-DE')} €</td>
                  <td className="py-2 px-3 text-rose-600">
                    -{Math.round(catConfig.yearRates[Math.min(4, idx)] * 100)}% p.a.
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ausführlicher Ratgeberteil & SEO-Text (E-E-A-T & LSI) */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10 shadow-sm mb-12 space-y-8">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-4">
            Wie wird der Restwert eines Autos berechnet? Formel & Methodik
          </h2>
          <p className="text-slate-600 text-sm md:text-base leading-relaxed mb-4">
            Der Wertverlust eines Kraftfahrzeugs verläuft nicht linear, sondern <strong>degressiv</strong>. Das bedeutet: In den ersten 12 bis 18 Monaten nach Erstzulassung verliert ein Neuwagen den größten prozentualen Anteil seines Kaufpreises – je nach Fahrzeugklasse zwischen <strong>18 % und 28 %</strong>. In den darauffolgenden Jahren flacht der jährliche Wertverlust deutlich ab und pendelt sich ab dem vierten Nutzungsjahr bei etwa <strong>5 % bis 8 % p.a.</strong> ein.
          </p>

          <div className="p-4 bg-slate-50 border-l-4 border-blue-600 rounded-r-xl my-4 text-xs md:text-sm text-slate-800 font-mono">
            <strong>Degressive Restwertformel:</strong><br />
            Restwert = Neupreis × (1 − Rate<sub>Jahr 1</sub>) × (1 − Rate<sub>Jahr 2</sub>) × … × (1 ± Kilometerfaktor) × Zustandsfaktor × Ausstattungsfaktor
          </div>

          <p className="text-slate-600 text-sm leading-relaxed">
            <strong>Rechenbeispiel Kompaktklasse:</strong> Ein VW Golf oder Ford Focus mit einem Neupreis von 35.000 € verliert im 1. Jahr typischerweise 22 %, im 2. Jahr 11 % und im 3. Jahr 9 %. Nach 3 Jahren bei durchschnittlicher Laufleistung verbleibt ein Restwert von:
            <br />
            <code className="text-xs bg-slate-100 px-2 py-0.5 rounded text-blue-800">35.000 € × 0,78 × 0,89 × 0,91 ≈ 22.110 € (ca. 63 % des Neupreises)</code>.
          </p>
        </div>

        {/* Vergleichstabelle Fahrzeugkategorien */}
        <div>
          <h3 className="text-lg font-bold text-slate-900 mb-3">
            Wertverlust-Tabelle nach Fahrzeugkategorie (Schwacke & DAT Durchschnitt)
          </h3>
          <p className="text-slate-600 text-xs md:text-sm mb-4">
            Die nachfolgende Tabelle zeigt die typische Wertstabilität in Prozent des ursprünglichen Bruttolistenpreises bei normaler Jahresfahrleistung und gepflegtem Zustand:
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border-collapse border border-slate-200 rounded-xl overflow-hidden">
              <thead className="bg-slate-100 text-slate-800 font-semibold">
                <tr>
                  <th className="py-2.5 px-3 border-b border-slate-200">Kategorie</th>
                  <th className="py-2.5 px-3 border-b border-slate-200">1 Jahr</th>
                  <th className="py-2.5 px-3 border-b border-slate-200">2 Jahre</th>
                  <th className="py-2.5 px-3 border-b border-slate-200">3 Jahre</th>
                  <th className="py-2.5 px-3 border-b border-slate-200">4 Jahre</th>
                  <th className="py-2.5 px-3 border-b border-slate-200">5 Jahre</th>
                  <th className="py-2.5 px-3 border-b border-slate-200">Besonderheiten</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="py-2.5 px-3 font-semibold text-slate-900">Kleinwagen</td>
                  <td className="py-2.5 px-3">76 %</td>
                  <td className="py-2.5 px-3">67 %</td>
                  <td className="py-2.5 px-3">60 %</td>
                  <td className="py-2.5 px-3">55 %</td>
                  <td className="py-2.5 px-3">52 %</td>
                  <td className="py-2.5 px-3 text-slate-500">Hohe Nachfrage im Stadtverkehr, geringe Fixkosten</td>
                </tr>
                <tr className="bg-slate-50/50">
                  <td className="py-2.5 px-3 font-semibold text-slate-900">Kompaktklasse</td>
                  <td className="py-2.5 px-3">78 %</td>
                  <td className="py-2.5 px-3">69 %</td>
                  <td className="py-2.5 px-3">63 %</td>
                  <td className="py-2.5 px-3">59 %</td>
                  <td className="py-2.5 px-3">55 %</td>
                  <td className="py-2.5 px-3 text-slate-500">Bester Kompromiss aus Werterhalt und Unterhaltskosten</td>
                </tr>
                <tr>
                  <td className="py-2.5 px-3 font-semibold text-slate-900">Mittelklasse</td>
                  <td className="py-2.5 px-3">80 %</td>
                  <td className="py-2.5 px-3">72 %</td>
                  <td className="py-2.5 px-3">66 %</td>
                  <td className="py-2.5 px-3">62 %</td>
                  <td className="py-2.5 px-3">59 %</td>
                  <td className="py-2.5 px-3 text-slate-500">Beliebte Dienstwagen; Diesel mit hohem Langstreckenanteil</td>
                </tr>
                <tr className="bg-slate-50/50">
                  <td className="py-2.5 px-3 font-semibold text-slate-900">Oberklasse</td>
                  <td className="py-2.5 px-3">75 %</td>
                  <td className="py-2.5 px-3">65 %</td>
                  <td className="py-2.5 px-3">59 %</td>
                  <td className="py-2.5 px-3">54 %</td>
                  <td className="py-2.5 px-3">51 %</td>
                  <td className="py-2.5 px-3 text-slate-500">Starker Wertverlust in den ersten 36 Monaten</td>
                </tr>
                <tr>
                  <td className="py-2.5 px-3 font-semibold text-slate-900">SUV & Geländewagen</td>
                  <td className="py-2.5 px-3">82 %</td>
                  <td className="py-2.5 px-3">74 %</td>
                  <td className="py-2.5 px-3">68 %</td>
                  <td className="py-2.5 px-3">64 %</td>
                  <td className="py-2.5 px-3">61 %</td>
                  <td className="py-2.5 px-3 text-slate-500">Sehr hohe Nachfrage; hohe Wertstabilität bei Benzin/Hybrid</td>
                </tr>
                <tr className="bg-slate-50/50">
                  <td className="py-2.5 px-3 font-semibold text-slate-900">Sportwagen</td>
                  <td className="py-2.5 px-3">85 %</td>
                  <td className="py-2.5 px-3">78 %</td>
                  <td className="py-2.5 px-3">73 %</td>
                  <td className="py-2.5 px-3">68 %</td>
                  <td className="py-2.5 px-3">65 %</td>
                  <td className="py-2.5 px-3 text-slate-500">Höchste Wertstabilität (besonders Porsche 911 / M-Modelle)</td>
                </tr>
                <tr>
                  <td className="py-2.5 px-3 font-semibold text-slate-900">Elektroauto (BEV) 2026/27</td>
                  <td className="py-2.5 px-3">72 %</td>
                  <td className="py-2.5 px-3">62 %</td>
                  <td className="py-2.5 px-3">56 %</td>
                  <td className="py-2.5 px-3">51 %</td>
                  <td className="py-2.5 px-3">48 %</td>
                  <td className="py-2.5 px-3 text-slate-500">Stark abhängig vom State of Health (SoH) der Batterie</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Wertbeeinflussende Faktoren */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-100">
          <div className="p-4 bg-emerald-50/60 border border-emerald-200 rounded-xl">
            <h4 className="text-sm font-bold text-emerald-900 flex items-center gap-1.5 mb-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              Wertsteigernde Faktoren (+5 % bis +15 %)
            </h4>
            <ul className="text-xs text-emerald-950 space-y-1.5 list-disc list-inside">
              <li><strong>Lückenloses Scheckheft</strong> beim Vertragshändler (+3 % bis +5 %)</li>
              <li><strong>Geringe Vorbesitzeranzahl</strong> (1. Hand bringt bis zu 500–1.500 € mehr)</li>
              <li><strong>Frische Hauptuntersuchung (TÜV &gt; 18 Monate)</strong> (+2 % bis +3 %)</li>
              <li><strong>Gefragte Ausstattungsmerkmale:</strong> LED/Matrix-Licht, Anhängerkupplung, Automatik</li>
              <li><strong>Nachgewiesene Unfallfreiheit</strong> mit Lackdicken-Messung</li>
              <li><strong>Bei E-Autos:</strong> Zertifizierter Akkutest (SoH &gt; 90 %)</li>
            </ul>
          </div>

          <div className="p-4 bg-rose-50/60 border border-rose-200 rounded-xl">
            <h4 className="text-sm font-bold text-rose-900 flex items-center gap-1.5 mb-2">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              Wertmindernde Faktoren (-5 % bis -25 %)
            </h4>
            <ul className="text-xs text-rose-950 space-y-1.5 list-disc list-inside">
              <li><strong>Überdurchschnittliche Laufleistung</strong> (Vielfahrerabschlag bis zu 25 %)</li>
              <li><strong>Raucherfahrzeug & Tierhaare</strong> (Aufbereitungskosten von 300–600 €)</li>
              <li><strong>Lückenhafte Wartungshistorie</strong> oder verpasste Zahnriemenwechsel</li>
              <li><strong>Unbeliebte Lackierungen</strong> (z.B. matt, gelb, knallgrün ohne Sondermodell)</li>
              <li><strong>Reparierte Vorschäden / Unfallwagen</strong> (merkantiler Minderwert)</li>
              <li><strong>Fällige Inspektion oder abgelaufene HU/AU</strong></li>
            </ul>
          </div>
        </div>

        {/* Händler-Einkauf vs. Marktpreis vs. Privatverkauf */}
        <div className="pt-4 border-t border-slate-100">
          <h3 className="text-lg font-bold text-slate-900 mb-3">
            Händler-Einkauf vs. Privatverkauf: Wo erziele ich den besten Preis?
          </h3>
          <p className="text-slate-600 text-xs md:text-sm leading-relaxed mb-3">
            Beim Autoverkauf stehen Fahrzeughalter vor der Entscheidung zwischen Bequemlichkeit und maximalem Erlös:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
            <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="font-bold text-slate-900 mb-1">Händler-Inzahlungnahme (ca. 85 %)</div>
              <p className="text-slate-600">
                Der Händler kalkuliert Standzeit, 12 Monate gesetzliche Sachmängelhaftung (Gewährleistung nach BGB) und Aufbereitung ein. Vorteil: Sofortige Barzahlung und Abmeldung ohne Risiko.
              </p>
            </div>
            <div className="p-3.5 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="font-bold text-blue-900 mb-1">Marktpreis (100 %)</div>
              <p className="text-blue-950">
                Der durchschnittliche Angebotspreis bei Autobörsen wie Mobile.de oder AutoScout24. Dient als fundierte Orientierungsgröße für Verhandlungen.
              </p>
            </div>
            <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl">
              <div className="font-bold text-emerald-900 mb-1">Privatverkauf (105 % – 110 %)</div>
              <p className="text-emerald-950">
                Hier erzielen Sie den höchsten Nettoerlös, da Sie die Händlermarge selbst einstreichen. Allerdings müssen Sie Probefahrten organisieren und einen rechtssicheren Kaufvertrag mit Gewährleistungsausschluss verwenden.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ-Bereich */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm mb-12">
        <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-blue-600" />
          Häufig gestellte Fragen (FAQ) zum Restwert & Wertverlust
        </h3>

        <div className="space-y-3">
          {[
            {
              q: 'Wie viel Wert verliert ein Neuwagen im ersten Jahr?',
              a: 'Im ersten Jahr verliert ein Neuwagen durchschnittlich ca. 20 bis 25 % seines ursprünglichen Bruttolistenpreises. Bereits bei der Zulassung und den ersten Kilometern verliert das Fahrzeug spürbar an Wert, da es formal zum Gebrauchtwagen wird. Oberklasse- und Elektrofahrzeuge verlieren tendenziell etwas mehr (bis zu 28 %), während begehrte Kompakt-SUVs oder Sportwagen oft nur 15 bis 18 % verlieren.'
            },
            {
              q: 'Was ist der Unterschied zwischen Marktwert, Händler-Einkaufswert und Schwacke-Liste?',
              a: 'Die Schwacke-Liste und DAT (Deutsche Automobil Treuhand) ermitteln statistische Händlereinkaufswerte (HEK) und Händlerverkaufswerte (HVK) auf Basis realer Transaktionsdaten. Der Marktwert ist der durchschnittliche Preis, zu dem vergleichbare Fahrzeuge auf dem freien Markt gehandelt werden. Der Händler-Einkaufspreis liegt typischerweise 12 bis 18 % unter dem Marktwert, da der Händler Kosten für Garantie, Aufbereitung und Gewinnmarge decken muss.'
            },
            {
              q: 'Wie beeinflusst der Kilometerstand den Autowert?',
              a: 'Die durchschnittliche Jahresfahrleistung in Deutschland liegt bei ca. 12.000 bis 15.000 km. Liegt Ihr Kilometerstand darunter (Wenigfahrer), erhöht sich der Restwert um ca. 3 bis 8 %. Bei deutlich überdurchschnittlichen Laufleistungen (Vielfahrer mit mehr als 25.000 km p.a.) sinkt der Restwert um 10 bis 25 %, da Verschleiß an Motor, Fahrwerk und Bremsen steigt.'
            },
            {
              q: 'Verlieren Elektroautos schneller an Wert als Verbrenner?',
              a: 'Bis 2024/2025 verloren reine Elektroautos durch schnelle Batterie-Entwicklung und den Wegfall staatlicher Förderungen etwas schneller an Wert als Verbrenner. Ab 2026 und mit Blick auf 2027 stabilisiert sich der Markt: Entscheidend ist der zertifizierte State of Health (SoH) der Hochvoltbatterie. Liegt dieser nachweislich über 90 %, erzielen Elektrofahrzeuge heute hervorragende Wiederverkaufspreise.'
            },
            {
              q: 'Was ist der Unterschied zwischen Markt-Restwert und steuerlichem Buchwert?',
              a: 'Der steuerliche Buchwert wird nach § 7 Abs. 1 EStG über eine feste lineare Nutzungsdauer von 6 Jahren (AfA-Tabelle) berechnet. Nach 6 Jahren beträgt der Buchwert steuerlich 1 € Erinnerungswert. Der reale Marktwert liegt nach 6 Jahren aber meist noch bei 35 % bis 50 % des Neupreises. Die Differenz zwischen Verkaufspreis und Buchwert stellt bei Firmenwagen einen steuerpflichtigen Buchgewinn (stille Reserve) dar.'
            },
            {
              q: 'Wie wird der Restwert beim Leasing ermittelt?',
              a: 'Beim Restwertleasing legt der Leasinggeber zu Vertragsbeginn einen kalkulatorischen Restwert fest, den das Auto am Ende der Laufzeit haben soll. Tritt am Ende ein Minderwert auf (z.B. durch Marktschwäche oder Schäden), muss der Leasingnehmer die Differenz nachzahlen. Beim heute üblichen Kilometerleasing trägt hingegen der Leasinggeber das Restwertrisiko.'
            },
            {
              q: 'Wie bereite ich mein Auto optimal auf den Verkauf vor?',
              a: 'Eine professionelle Fahrzeugaufbereitung (Politur, Innenraum-Tiefenreinigung) für 150 bis 250 € steigert den erzielbaren Verkaufspreis häufig um 500 bis 1.000 €. Lassen Sie kleine Parkdellen per Smart-Repair entfernen, legen Sie alle TÜV-Berichte und Werkstattrechnungen sortiert vor und inserieren Sie mit hochauflösenden Fotos bei gutem Tageslicht.'
            }
          ].map((item, idx) => (
            <div 
              key={idx} 
              className="border border-slate-200 rounded-xl overflow-hidden transition-all"
            >
              <button
                onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                className="w-full flex justify-between items-center p-4 text-left font-medium text-slate-900 hover:bg-slate-50 transition-colors text-sm md:text-base"
              >
                <span>{item.q}</span>
                {activeFaq === idx ? (
                  <ChevronUp className="w-4 h-4 text-slate-500 shrink-0 ml-2" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-500 shrink-0 ml-2" />
                )}
              </button>
              {activeFaq === idx && (
                <div className="p-4 pt-0 text-xs md:text-sm text-slate-600 leading-relaxed border-t border-slate-100 bg-slate-50/50">
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Embed Code Modal / Banner */}
      <div className="mb-12">
        <EmbedCalloutBanner 
          calcId="restwert-rechner"
          calcName="Restwert-Rechner: Auto-Wertverlust berechnen"
          calcPath="/restwert-rechner" 
          defaultHeight={950}
          navigateTo={navigateTo}
        />

        {/* Direkt-Einbettungsbox für Publisher */}
        <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <Share2 className="w-3.5 h-3.5 text-blue-600" />
              Rechner kostenlos auf eigener Website einbinden (iFrame-Code):
            </span>
            <button
              onClick={copyEmbedToClipboard}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              {copiedEmbed ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              {copiedEmbed ? 'Kopiert!' : 'Code kopieren'}
            </button>
          </div>
          <code className="block p-2.5 bg-white border border-slate-200 rounded-lg text-[11px] text-slate-700 font-mono break-all select-all">
            {embedCodeString}
          </code>
        </div>
      </div>

      {/* Bewertungen */}
      <div className="mb-12">
        <Reviews calculatorId="restwert" />
      </div>

      {/* Verwandte Rechner */}
      <RelatedCalculators 
        currentPath="/restwert-rechner" 
        onNavigate={(path) => navigateTo ? navigateTo(path) : onBackToPortal?.()}
        defaultCategory="all"
        title="Verwandte Rechner rund um Auto, Mobilität & Finanzen"
        subtitle="Berechnen Sie Kfz-Steuer, Leasingraten, Autofinanzierung oder Firmenwagen-Besteuerung:"
      />
    </div>
  );
}
